import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  SidebarFooterLine,
  SidebarFooterRow,
} from '@/components/blocks/Layout/Sidebar/SidebarFooterRow';
import { Button } from '@/components/ui/Button';
import { ChangelogSidebarRow } from '@/features/changelog';
import { useWrappedReportPrefetch } from '@/features/growth';
import { isReferralBannerVisible } from '@/features/referral';
import {
  subscriptionStore,
  UsageStatusSidebarCard,
} from '@/features/subscription';
import { useHashRoute } from '@/hooks/useHashRoute';
import { littlebirdSdk } from '@/lib/sdk-init';
import { uiStore } from '@/stores/ui.store';
import { updateStore } from '@/stores/updateStore';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

export const NavRailFooterCards = observer(() => {
  const aiWrappedSidebarEnabled = false;
  const isFreemiumUser = userStore.user?.feature_flags?.is_freemium_user;
  const showUpgradeCard = !subscriptionStore.isPlus && !isFreemiumUser;

  const isUpdateReady = updateStore.shouldShowToast;

  return (
    <>
      {!showUpgradeCard && !isUpdateReady && (
        <>
          {aiWrappedSidebarEnabled && <WrappedCard />}
          <ReferralCard />
        </>
      )}
      <ChatLoadingStatus />
      <ChangelogSidebarRow />
      {showUpgradeCard && <UpgradePlanCard />}
      <UsageStatusSidebarCard />
    </>
  );
});

const ChatLoadingStatus = () => {
  const [isLoadingChats, setIsLoadingChats] = useState(
    littlebirdSdk.storage.fullBootstrapPending
  );

  useEffect(() => {
    const updateStatus = () =>
      setIsLoadingChats(littlebirdSdk.storage.fullBootstrapPending);
    const interval = window.setInterval(updateStatus, 1000);
    return () => window.clearInterval(interval);
  }, []);

  if (!isLoadingChats) return null;

  return (
    <SidebarFooterRow
      role="status"
      icon={
        <Icons.loader className="size-3 animate-spin text-foreground-muted" />
      }
      label="Syncing more chats…"
    />
  );
};

const ReferralCard = observer(() => {
  const { setHash } = useHashRoute();

  if (!isReferralBannerVisible()) return null;

  return (
    <SidebarFooterRow
      className="group bg-gradient-to-r from-brand-jordy-blueA/10 to-brand-plum/10"
      dismissLabel="Dismiss referral offer"
      icon={
        <Icons.refer className="size-3.5 text-shell-accent origin-bottom motion-safe:group-hover:animate-gift-wiggle motion-safe:group-focus-visible:animate-gift-wiggle" />
      }
      label={
        subscriptionStore.state.userReferralRewardType === 'activity_based'
          ? 'Get 1 Month Free'
          : 'Get 2 Months Free'
      }
      onClick={() => {
        trackEvent('REFERRAL_MENU_CLICKED');
        setHash('invite');
      }}
      onDismiss={() => {
        trackEvent('REFERRAL_CTA_DISMISSED', { source: 'sidebar' });
        uiStore.dismissReferralCard();
      }}
    />
  );
});

const WrappedCard = observer(() => {
  const { setHash } = useHashRoute();
  useWrappedReportPrefetch();

  if (uiStore.aiWrappedCardDismissed) return null;

  return (
    <SidebarFooterRow
      className="bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-violet-500/10"
      icon={<Icons.sparkles className="size-3.5 text-foreground-muted" />}
      label="Your AI Wrapped"
      dismissLabel="Dismiss AI Wrapped"
      onClick={() => {
        trackEvent('AI_WRAPPED_SIDEBAR_CLICKED');
        setHash('ai-wrapped');
      }}
      onDismiss={() => {
        trackEvent('AI_WRAPPED_SIDEBAR_DISMISSED');
        uiStore.dismissAiWrappedCard();
      }}
    />
  );
});

const UpgradePlanCard = observer(() => {
  const { setHash } = useHashRoute();
  const user = userStore.user;
  const isFreemiumUser = user?.feature_flags?.is_freemium_user;

  if (isFreemiumUser || subscriptionStore.isPlus) return null;

  return (
    <SidebarFooterRow
      icon={<Icons.circleArrowUp className="size-3.5 text-foreground-muted" />}
      label="Trial Expired"
      description="Upgrade"
      trailing={
        <Button
          variant="default"
          size="dense"
          onClick={() => setHash('settings/Subscription')}
        >
          Upgrade
        </Button>
      }
    >
      <SidebarFooterLine
        icon={<Icons.refer className="size-3.5 text-shell-accent" />}
        label={
          subscriptionStore.state.userReferralRewardType === 'activity_based'
            ? 'Get 1 Month Free'
            : 'Get 2 Months Free'
        }
        onClick={() => {
          trackEvent('REFERRAL_MENU_CLICKED');
          setHash('invite');
        }}
      />
    </SidebarFooterRow>
  );
});
